import { BellFilled, CalendarOutlined, CloseOutlined, ReloadOutlined } from '@ant-design/icons';
import { Badge, DatePicker, notification, theme } from 'antd';
import { authSlice } from 'common/commonRedux/auth/slice';
import { filterData, setFilterData } from 'common/commonRedux/feature/filteringSlice';
import { useGetDataQuery } from 'common/commonRedux/getRTKApi/baseApi';
import { BreadcrumbNav } from 'common/components';
import { Humberger } from 'common/customIcons';
import { useDepartment, useIsDark } from 'common/hooks';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { startTaskHub, stopTaskHub } from 'common/realtime/taskHub';
import { eventText, onTaskStatusEvents, startTaskStatusFeed, stopTaskStatusFeed, taskStatusApi } from 'common/realtime/taskStatusFeed';
import { armSystemNotifications, notifySystem } from 'common/realtime/systemNotify';
import LogoutPopup from './LogoutPopup';
import Notificaton from './Notificaton/Notificaton';
import MaleAvatar from './MaleAvatar.png';
import './Topbar.scss';
import LogoImg from './trackforce_logo_one.svg';
const { useToken } = theme;
const CLAY_CHIP_SHADOW =
  '5px 5px 12px rgba(166, 178, 207, 0.45), -4px -4px 10px rgba(255, 255, 255, 0.9), inset 1px 1px 1px rgba(255, 255, 255, 0.7), inset -1px -1px 1px rgba(166, 178, 207, 0.18)';
const CLAY_CHIP_SHADOW_DARK =
  '5px 5px 12px rgba(0, 0, 0, 0.55), -4px -4px 10px rgba(255, 255, 255, 0.03), inset 1px 1px 1px rgba(255, 255, 255, 0.05), inset -1px -1px 1px rgba(0, 0, 0, 0.35)';

const Topbar = () => {
  const { t } = useTranslation();
  const isDark = useIsDark();
  const [openNotification, setOpenNotification] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [supervisor, setSupervisor] = useState<string | undefined>();
  const location = useLocation();
  const isDashboardRoute = location.pathname === '/';
  const { departments } = useDepartment();
  const getFilterData = useSelector(filterData);
  const departmentOptions = departments?.map((d: any) => ({
    label: d.strDepartmentName,
    value: d.intDepartmentId,
  }));
  const { isAuth, profileData, isMenuClicked, commonRefreshObj, originalTokenData } =
    useSelector((state: any) => state.auth, shallowEqual);
  // Sidebar/topbar profile must reflect the real logged-in user, not the
  // impersonated account.
  const realAccountId = originalTokenData?.accountid ?? profileData?.tokenData?.accountid;

  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const unreadRef = useRef<number | null>(null); // last count the UI knows; null = not loaded yet
  useEffect(() => { unreadRef.current = unread; }, [unread]);
  const hasToken = !!profileData?.token;
  const feedUserKey = `${profileData?.tokenData?.accountid}:${profileData?.tokenData?.userid}`;

  // One hub connection + status feed; restarts on login / logout / impersonation.
  useEffect(() => {
    if (!isAuth || !hasToken) return;
    armSystemNotifications();
    startTaskHub();
    startTaskStatusFeed(feedUserKey);
    return () => { stopTaskStatusFeed(); stopTaskHub(); };
  }, [isAuth, hasToken, feedUserKey]);

  // Badge + toasts.
  useEffect(() => {
    if (!isAuth || !hasToken) return;
    let alive = true;
    const refreshUnread = () =>
      taskStatusApi.unread().then((d) => {
        if (!alive) return;
        const next = d.unreadCount ?? 0;
        // Safety net: the count went up without a live push reaching us
        // (socket down / blocked), so still chime and show an OS notice.
        if (unreadRef.current !== null && loadedOnce && next > unreadRef.current) {
          notifySystem({
            title: 'TrackForce',
            body: t('layout:taskStatus.title'),
            tag: `tf-task-status-count-${next}`,
          });
        }
        loadedOnce = true;
        unreadRef.current = next;
        setUnread(next);
      }).catch(() => {});
    let loadedOnce = false;
    refreshUnread();
    const poll = window.setInterval(refreshUnread, 30000);
    const onFocus = () => { if (document.visibilityState === 'visible') refreshUnread(); };
    document.addEventListener('visibilitychange', onFocus);

    const off = onTaskStatusEvents((events, { replay }) => {
      // A replay may re-deliver events the server's unreadCount already includes,
      // so ask the server after a replay; only live pushes are counted locally.
      if (replay) refreshUnread();
      else setUnread((n) => n + events.length);

      // OS notification + chime for every event type (a replay is summarised).
      if (events.length > 3) {
        notifySystem({
          title: 'TrackForce',
          body: t('layout:taskStatus.whileAway', { count: events.length }),
          tag: `tf-task-status-${events[events.length - 1].seq}`,
        });
      } else {
        events.forEach((ev) => notifySystem({
          title: `${ev.displayId ?? ''} ${ev.taskName}`.trim(),
          body: eventText(ev, t),
          tag: `tf-task-status-${ev.seq}`,
          onClick: () => navigate(`/manage-project/task-details/${ev.taskId}`),
        }));
      }

      if (document.visibilityState !== 'visible') return; // one toast per visible tab
      if (events.length > 3) {
        notification.info({ message: t('layout:taskStatus.whileAway', { count: events.length }) });
        return;
      }
      events.forEach((ev) => notification.info({
        message: `${ev.displayId ?? ''} ${ev.taskName}`.trim(),
        description: eventText(ev, t),
        onClick: () => navigate(`/manage-project/task-details/${ev.taskId}`),
      }));
    });
    return () => {
      alive = false;
      off();
      window.clearInterval(poll);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [isAuth, hasToken, feedUserKey]);

  const { data: organizationData = {} } = useGetDataQuery(
    {
      urlObjKey: 'GetOrganizationProfile',
      params: { accountId: realAccountId },
      useOriginalToken: true,
    },
    { skip: !realAccountId }
  );

  const { setIsMenuClicked, setLayoutCollapsed } = authSlice.actions;
  const dispatch: any = useDispatch();
  const notificationRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const topbarRef = useRef<HTMLDivElement | null>(null);
  const { token } = useToken();

  // Handle Click Outside for both Popups
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (notificationRef.current && !notificationRef.current.contains(target)) {
        setOpenNotification(false);
      }
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setOpenDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleRefresh = () => {
    if (commonRefreshObj?.CB) {
      setIsRefreshing(true);
      commonRefreshObj.CB();
      setTimeout(() => setIsRefreshing(false), 600);
    }
  };

  const AvatarImage = () => {
    return organizationData?.profileImgUrl ? (
      <img
        style={{ border: `1px solid ${token.colorBorderSecondary}` }}
        className="size-[28px] rounded-full object-cover"
        src={organizationData?.profileImgUrl}
        alt="Profile"
      />
    ) : (
      <img
        className="size-[28px] rounded-full object-contain"
        src={MaleAvatar}
        alt="Default Avatar"
      />
    );
  };

  return (
    <div
      style={{ borderBottom: isAuth && isDashboardRoute ? "none" : `1px solid ${token.colorBorder}`, background: token.colorBgBase }}
      ref={topbarRef}
      className={`topbar flex justify-between items-center`}
    >
      {/* LEFT SIDE: Mobile Menu / Breadcrumb (+ dashboard filters) */}
      <div className="flex items-center gap-2">
        <button
          className="xl:hidden w-8 h-8 flex justify-center items-center bg-primaryColor rounded text-white"
          onClick={(e) => {
            e.stopPropagation();
            const newVal = !isMenuClicked;
            dispatch(setIsMenuClicked(newVal));
            if (newVal) dispatch(setLayoutCollapsed(false));
          }}
        >
          {isMenuClicked ? <CloseOutlined /> : <Humberger />}
        </button>
        <Link to={'/'}>
          <img
            src={LogoImg}
            alt="TrackForce"
            className="w-[120px] md:w-[200px] block xl:hidden"
          />
        </Link>
        <div className="hidden xl:block">
          <BreadcrumbNav />
        </div>

        {isAuth && isDashboardRoute && (
          <div className="dashboard-topbar-filters hidden md:flex items-center gap-1.5 ml-2">
            {/* <div
              className="flex items-center h-[28px] px-1.5 rounded-[8px] bg-white"
              style={{ boxShadow: CLAY_CHIP_SHADOW }}
            >
              <Select
                placeholder="Department"
                value={getFilterData?.departmentName || undefined}
                options={departmentOptions}
                onChange={(_, data: any) =>
                  dispatch(
                    setFilterData({
                      departmentName: data?.label,
                      departmentId: data?.value,
                    })
                  )
                }
                allowClear
                variant="borderless"
                suffixIcon={<DownOutlined style={{ fontSize: 9, color: '#64748B' }} />}
                style={{ width: 100 }}
                size="small"
              />
            </div>

            <div
              className="flex items-center h-[28px] px-1.5 rounded-[8px] bg-white"
              style={{ boxShadow: CLAY_CHIP_SHADOW }}
            >
              <Select
                placeholder="Supervisor"
                value={supervisor}
                onChange={(v) => setSupervisor(v)}
                options={[{ label: 'All', value: 'all' }]}
                allowClear
                variant="borderless"
                suffixIcon={<DownOutlined style={{ fontSize: 9, color: '#64748B' }} />}
                style={{ width: 100 }}
                size="small"
              />
            </div> */}

            <div
              className={`flex items-center h-[24px] px-1.5 rounded-[8px] ${isDark ? 'bg-[#0F1E33]' : 'bg-white'}`}
              style={{ boxShadow: isDark ? CLAY_CHIP_SHADOW_DARK : CLAY_CHIP_SHADOW }}
            >
              <DatePicker.RangePicker
                value={getFilterData?.dateRange}
                onChange={(value) => {
                  if (!value) {
                    // 1. If cleared (value is null), calculate the last 7 days
                    const defaultRange = [dayjs().subtract(7, 'day'), dayjs()];
                    dispatch(setFilterData({ dateRange: defaultRange }));
                  } else {
                    // 2. Otherwise, save the user's selected range
                    dispatch(setFilterData({ dateRange: value }));
                  }
                }}
                format="D/M/YYYY"
                variant="borderless"
                suffixIcon={<CalendarOutlined style={{ fontSize: 11, color: isDark ? '#CBD5E1' : '#64748B' }} />}
                size="small"
                className="w-full md:w-[200px] rounded-[4px] "
                style={{ height: 36, width: '100%', borderRadius: 4 }}
                popupClassName="filtering-top-bar-dropdown"
              />
            </div>
          </div>
        )}
      </div>

      {/* RIGHT SIDE: Refresh + Notification + Org Info */}
      {isAuth && (
        <div className="flex items-center gap-0">
          {/* Global Refresh Button */}
          <button
            className={`flex items-center justify-center w-[32px] h-[32px] rounded-md text-[#94A3B8] hover:text-[#1B73E8] transition-all duration-200 border-0 bg-transparent cursor-pointer ${isDark ? 'hover:bg-[#1B73E81A]' : 'hover:bg-[#EFF6FF]'}`}
            onClick={handleRefresh}
            title={t('layout:topbar.refreshPage')}
          >
            <ReloadOutlined className={`text-md ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>

          {/* Notification Bell */}
          <div ref={notificationRef} className="relative">
            <button
              className={`flex items-center justify-center w-[32px] h-[32px] rounded-md text-[#94A3B8] hover:text-[#1B73E8] transition-all duration-200 border-0 bg-transparent cursor-pointer ${isDark ? 'hover:bg-[#1B73E81A]' : 'hover:bg-[#EFF6FF]'}`}
              onClick={() => setOpenNotification(!openNotification)}
            >
              <Badge count={unread} overflowCount={99} size="small">
                <BellFilled className="text-md" />
              </Badge>
            </button>
            {openNotification && (
              <div className={`notification_panel ${isDark ? 'dark' : ''}`}>
                <Notificaton onUnreadChange={setUnread} onNavigate={() => setOpenNotification(false)} />
              </div>
            )}
          </div>

          {/* Divider */}
          <div className={`h-[20px] w-px mx-[10px] ${isDark ? 'bg-[#2E3138]' : 'bg-[#E2E8F0]'}`} />

          {/* Org Avatar + Name + Arrow */}
          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setOpenDropdown((prev) => !prev)}
              className={`flex items-center gap-[8px] cursor-pointer rounded-lg px-2 py-1.5 transition-all duration-200 border-0 bg-transparent ${isDark ? 'hover:bg-[#1E293B]' : 'hover:bg-[#F8FAFC]'}`}
            >
              <AvatarImage />
              <span className={`hidden md:inline text-sm font-medium max-w-[120px] truncate ${isDark ? 'text-[#CBD5E1]' : 'text-[#334155]'}`}>
                {originalTokenData ? originalTokenData?.orgName || t('layout:topbar.organization') : profileData?.tokenData?.orgName || t('layout:topbar.user')}
              </span>
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                className={`hidden md:block transition-transform duration-200 ${openDropdown ? 'rotate-180' : ''}`}
              >
                <path d="M3 4.5L6 7.5L9 4.5" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {openDropdown && (
              <LogoutPopup closePopup={() => setOpenDropdown(false)} profileImage={organizationData?.profileImgUrl || ''} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Topbar;
